import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { JRCThemeProvider } from '../JRCThemeProvider/JRCThemeProvider';
import { JRCModalBox, JRCModalContainer, JRCModalContainerFull, ModalBorderRadius } from './JRCModal.styles';

export type ModalVariant = 'full-width' | 'full-screen' | 'full-height' | 'fixed-height';

export interface JRCModalLayoutProps {
    open: boolean;
    closeHandler: () => void;
    children?: ReactNode;
    enableClickAwayCloseModal?: boolean;
    radius?: ModalBorderRadius;
    portalId?: string;
    inPlace?: boolean;
    className?: string;
    maxHeight?: string;
    variant?: ModalVariant;
    overflow?: 'visible' | 'hidden';
    zIndex?: number;
    minHeight?: string;
}

interface JRCModalContainersProps extends React.HTMLAttributes<HTMLElement> {
    radius?: ModalBorderRadius;
    maxHeight?: string;
    variant?: ModalVariant;
    overflow?: 'hidden' | 'visible';
}

const JRCModalContainers = ({ variant, overflow, maxHeight, ...props }: JRCModalContainersProps) => {
    switch (variant) {
        case 'full-width':
        case 'full-screen':
            return <JRCModalContainerFull {...props} isFullScreen={variant === 'full-screen'} maxHeight={maxHeight} />;
        case 'full-height':
        case 'fixed-height':
        default:
            return (
                <JRCModalContainer {...props} maxHeight={maxHeight} variant={variant} overflow={overflow ?? 'hidden'} />
            );
    }
};

const ModalBoxElement = (
    {
        closeHandler,
        children,
        enableClickAwayCloseModal,
        radius = 'medium',
        className,
        maxHeight,
        variant,
        overflow,
        zIndex,
    }: Omit<JRCModalLayoutProps, 'open' | 'portalId' | 'inPlace'>,
    ref: React.ForwardedRef<HTMLDivElement>,
) => {
    const handleClickAway = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!enableClickAwayCloseModal) {
            return;
        }
        event.stopPropagation();
        closeHandler();
    };
    const handleClickAwayChild = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    };
    const escapeHandler = (e: React.KeyboardEvent) => {
        if (e.key == 'Escape' && enableClickAwayCloseModal) closeHandler();
    };

    return (
        <JRCModalBox
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => escapeHandler(e)}
            tabIndex={0}
            overflow={overflow}
            ref={ref}
            className={className}
            isFullHeight={variant === 'full-height' || variant === 'full-screen'}
            zIndex={zIndex}
            onClick={handleClickAway}>
            <JRCModalContainers
                overflow={overflow}
                className="modal-container"
                radius={radius}
                maxHeight={maxHeight ?? '1000px'}
                variant={variant}
                onClick={handleClickAwayChild}>
                {children}
            </JRCModalContainers>
        </JRCModalBox>
    );
};

const ModalBox = React.forwardRef(ModalBoxElement);

const createModalElement = (parent: HTMLElement, id: string): HTMLDivElement => {
    let modal = document.getElementById(id) as HTMLDivElement;

    if (!modal) {
        modal = document.createElement('div');
        modal.id = uuidv4();
        parent.appendChild(modal);
    }

    return modal;
};

const ModelLayout = (
    { open, portalId = 'react-modal', inPlace = false, ...props }: JRCModalLayoutProps,
    ref: React.ForwardedRef<HTMLDivElement>,
) => {
    const portalDiv = document.getElementById(portalId);
    const [id] = React.useState(() => uuidv4());
    const modal = React.useMemo(
        () => open && !inPlace && portalDiv && createModalElement(portalDiv, id),
        [open, inPlace, portalDiv, id],
    );

    if (open) {
        if (inPlace) {
            return <ModalBox {...props} ref={ref} zIndex={props.zIndex} />;
        } else if (modal) {
            // when using a portal, we need to provide the Theme explicitly
            return ReactDOM.createPortal(
                <JRCThemeProvider>
                    <ModalBox {...props} ref={ref} />
                </JRCThemeProvider>,
                modal,
            );
        }
    }
    return null;
};

export const JRCModalLayout = React.forwardRef(ModelLayout);
